import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import { type SafeString, htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { IntlService } from 'ember-intl';
import { getLogger } from 'log4javascript';
import type ApplicationController from 'login/controllers/application';
import type { ApplicationModel } from 'login/routes/application';
import { WFlashMessagesContainer } from 'w-flash-messages';
import FlashMessagesService from 'w-flash-messages/services/flash-messages-service';

const log = getLogger('login');

export default class Application extends Component<{
  Args: { model: ApplicationModel; controller: ApplicationController };
}> {
  @service private flashMessagesService!: FlashMessagesService;
  @service private intl!: IntlService;

  @action private setup(): void {
    window.addEventListener('resize', this.didResize);
    this.didResize();
  }

  @tracked private width = 0;
  @tracked private height = 0;

  @action private didResize(): void {
    const isAndroid = navigator.userAgent.toLowerCase().indexOf('android') > -1;
    if (isAndroid) {
      // for android devices appearance of the virtual keyboard can inadvertently change from portrait
      // mode to landscape mode to avoid this we have to use screen dims (not influenced by the
      // virtual keyboard) (the problem does not occur on IOS, where screen dims even do not seem to
      // work: there we keep using window dims)
      this.width = screen.availWidth;
      this.height = screen.availHeight;
    } else {
      this.width = window.innerWidth;
      this.height = innerHeight;
    }
  }

  private get backgroundUrl(): string {
    const logonParams = this.args.model.logonParams;
    const wallpaper =
      this.width > this.height ? logonParams.wallpaperLandscape : logonParams.wallpaperPortrait;

    return wallpaper;
  }

  private get style(): SafeString {
    const style = this.args.model.logonParams.useWallpapers
      ? `background-image: url(${this.backgroundUrl});`
      : '';
    return htmlSafe(style);
  }

  @action private lecChanged(): void {
    const lec = this.args.controller.lec;
    if (lec === null || lec.length === 0) {
      return;
    }
    const message = this.intl.t(`login.lec.${lec}`);
    let type: 'information' | 'warning' | 'error';
    let duration = 0;
    switch (lec) {
      case 'logout':
        type = 'information';
        duration = 5000;
        break;
      case 'sessionclosed':
      case 'timedout':
      case 'forgotpasswordok':
        type = 'information';
        break;
      case 'invalid':
      case 'unknown':
      case 'expired':
        type = 'warning';
        break;
      case 'duplicateEmail':
      case 'syntaxEmail':
      case 'noenterprise':
      case 'error':
        type = 'error';
        break;
      default:
        log.error(`Not supported error message: ${lec}`);
        return;
    }
    this.flashMessagesService.add({ message, type, duration });
  }

  <template>
    <div
      class='login'
      style={{this.style}}
      {{didInsert this.setup}}
      {{didInsert this.lecChanged}}
      {{didUpdate this.lecChanged @controller.lec}}
    >
      {{outlet}}
    </div>

    <WFlashMessagesContainer class='topRight' />
  </template>
}
